<template>
  <MobileAuthLayout>
    <template #default="{ facilityId }">
      {{ updateFacilityId(facilityId) }}

      <div class="mobile-inventory-content">
        <div v-if="currentScreen === 'scan'" class="screen-container">
          <div class="screen-header">
            <h2>Scan Inventory Device</h2>
            <p>
              Point your camera at the barcode on the first device you want to
              install.
            </p>
          </div>

          <div class="barcode-icon">
            <v-icon size="80" color="grey">fas fa-barcode</v-icon>
          </div>

          <div class="scan-actions">
            <v-btn
              color="primary"
              large
              block
              class="mb-4"
              @click="startDeviceScanning"
            >
              <v-icon left>fas fa-camera</v-icon>
              Scan Device
            </v-btn>

            <p class="text-center mb-4 grey--text">OR</p>

            <v-text-field
              v-model="manualDeviceId"
              placeholder="Enter Device ID manually (e.g., CF00510)"
              outlined
              dense
              class="mb-4"
              @keydown="onDeviceFieldKeydown"
              @keyup.enter="onDeviceFieldEnter"
            />

            <v-btn color="black" outlined block @click="searchDeviceById">
              Search by ID
            </v-btn>

            <v-btn
              v-if="hasAssignments"
              color="success"
              outlined
              block
              class="mt-4"
              @click="goToSummary"
            >
              Complete Assignment ({{ assignmentSummary.totalAssignments }}
              devices)
            </v-btn>
          </div>
        </div>

        <div v-if="currentScreen === 'scanner'" class="scanner-screen">
          <div class="scanner-header">
            <v-btn icon @click="currentScreen = 'scan'">
              <v-icon color="white">fas fa-arrow-left</v-icon>
            </v-btn>
            <span>Scan Device QR Code</span>
          </div>

          <QRCodeScanner
            ref="qrScanner"
            :advanced="true"
            @detect-device="onDeviceScanned"
            @detect="onQRDetected"
          />
        </div>

        <div v-if="currentScreen === 'assign'" class="screen-container">
          <div class="screen-header">
            <div class="d-flex">
              <v-btn
                color="primary"
                align="left"
                small
                class="mr-4 mt-2"
                @click="scanNextDevice"
              >
                Go Back
              </v-btn>
              <h2>Assign to Unit</h2>
            </div>
            <p v-if="selectedDevice">
              Device {{ formatDeviceName(selectedDevice) }} is ready. Start
              typing a unit number to assign it.
            </p>
          </div>

          <div v-if="selectedDevice" class="selected-device-info">
            <div class="device-card">
              <div class="device-info">
                <div class="device-name">
                  <strong>Device ID:</strong>
                  {{ formatDeviceName(selectedDevice) }}
                </div>
                <div class="d-flex align-center justify-space-between mt-2">
                  <strong
                    >Device Status:
                    <v-chip
                      x-small
                      :color="getStatusColor(selectedDevice.status)"
                    >
                      {{ selectedDevice.status }}
                    </v-chip></strong
                  >

                  <span class="device-type"
                    ><strong>Device Type: </strong
                    >{{
                      selectedDevice.type == "xp" ? "SMU" : selectedDevice.type
                    }}</span
                  >
                </div>
              </div>
            </div>
          </div>

          <div class="unit-search-section">
            <v-autocomplete
              v-model="selectedUnitId"
              :items="filteredUnits"
              :search-input.sync="unitSearchText"
              item-text="name"
              item-value="id"
              placeholder="Start typing unit number (e.g., A101)"
              outlined
              dense
              clearable
              no-filter
              hide-details
              class="unit-search"
            >
              <template #item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.devices?.length || 0 }} devices assigned
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </div>

          <div class="assign-actions">
            <v-btn
              v-if="selectedUnitId"
              color="success"
              large
              block
              :loading="assignmentLoading"
              @click="assignDeviceToUnit"
            >
              Assign Device
            </v-btn>
          </div>
        </div>

        <div v-if="currentScreen === 'assigned'" class="screen-container">
          <div class="success-header">
            <v-icon size="64" color="success">fas fa-check-circle</v-icon>
            <h2>Device Assigned!</h2>
          </div>

          <div class="assignment-summary">
            <div class="summary-item">
              <strong>Unit:</strong> {{ assignedUnit?.name }}
            </div>
            <div class="summary-item">
              <strong>Device:</strong> {{ formatDeviceName(assignedDevice) }}
            </div>
          </div>

          <div class="continue-message">
            <p>Continue to the next device or complete the task.</p>
          </div>

          <div class="next-actions">
            <v-btn
              color="primary"
              large
              block
              class="mb-3"
              @click="scanNextDevice"
            >
              <v-icon left>fas fa-qrcode</v-icon>
              Scan Next Device
            </v-btn>

            <v-btn color="grey" outlined block @click="completeAssignment">
              Complete
            </v-btn>
          </div>
        </div>

        <div v-if="currentScreen === 'summary'" class="screen-container">
          <div class="summary-header">
            <h2>Assignment Summary</h2>
            <p>
              {{ assignmentSummary.totalAssignments }} devices assigned
              successfully
            </p>
          </div>

          <div class="assignment-list">
            <div
              v-for="assignment in assignmentHistory"
              :key="assignment.id"
              class="assignment-item"
            >
              <div class="assignment-details">
                <div class="unit-name">{{ assignment.unitName }}</div>
                <div class="device-name">{{ assignment.deviceName }}</div>
              </div>
              <v-icon color="success">fas fa-check</v-icon>
            </div>
          </div>

          <div class="notification-section">
            <h3 class="notification-title">Send Notification</h3>
            <p class="notification-description">
              Send assignment summary to key contacts via email.
            </p>

            <div class="email-input-section">
              <v-text-field
                v-model="emailInput"
                label="Enter email address"
                placeholder="example@company.com"
                outlined
                dense
                class="mb-2"
                @keyup.enter="addEmail"
              >
                <template #append>
                  <v-btn
                    small
                    color="primary"
                    :disabled="!isValidEmail(emailInput)"
                    @click="addEmail"
                  >
                    Add
                  </v-btn>
                </template>
              </v-text-field>

              <div v-if="emailList.length > 0" class="email-list">
                <v-chip
                  v-for="(email, index) in emailList"
                  :key="index"
                  close
                  small
                  class="mr-2 mb-2"
                  @click:close="removeEmail(index)"
                >
                  {{ email }}
                </v-chip>
              </div>
            </div>

            <v-text-field
              v-model="doneByInput"
              label="Assigned by "
              placeholder="Enter your name"
              outlined
              dense
              class="mb-4"
            />
          </div>

          <div class="summary-actions">
            <v-btn
              color="success"
              large
              block
              class="mb-3"
              :disabled="!canSendNotification"
              :loading="sendingNotification"
              @click="sendNotification"
            >
              <v-icon left>fas fa-paper-plane</v-icon>
              Send Notification ({{ emailList.length }} recipients)
            </v-btn>

            <v-btn
              color="primary"
              large
              block
              class="mb-3"
              @click="scanNextDevice"
            >
              <v-icon left>fas fa-qrcode</v-icon>
              Scan More Devices
            </v-btn>

            <v-btn color="grey" outlined block @click="finishAndClear">
              Finish
            </v-btn>
          </div>
        </div>

        <v-snackbar v-model="showMessage" :color="messageType" timeout="4000">
          <strong>{{ message }}</strong>
          <template #action="{ attrs }">
            <v-btn text v-bind="attrs" @click="showMessage = false">
              Close
            </v-btn>
          </template>
        </v-snackbar>
      </div>
    </template>
  </MobileAuthLayout>
</template>

<script>
import { ref, computed } from "vue";
import MobileAuthLayout from "../../../mobile/MobileAuthLayout.vue";
import QRCodeScanner from "../../../../../tod-ui/components/QRCodeScanner.vue";
import {
  ORGANIZATION_DEVICES,
  INVENTORY_SMART_UNITS,
  UPDATE_DEVICE,
  SEND_PREINSTALLED_DEVICE_MOBILE_SUMMARY,
} from "../../graphql";
import useInventory from "../../useInventory";
import useMobileAssignmentStorage from "./useMobileAssignmentStorage";

export default {
  name: "PageMobileInventory",
  components: {
    MobileAuthLayout,
    QRCodeScanner,
  },
  setup() {
    const { formatDeviceName } = useInventory();

    const {
      assignmentHistory,
      assignmentSummary,
      addAssignment,
      clearAssignments,
      generateAssignmentReport,
    } = useMobileAssignmentStorage();

    const currentScreen = ref("scan");
    const selectedDevice = ref(null);
    const selectedUnitId = ref(null);
    const assignedDevice = ref(null);
    const assignedUnit = ref(null);
    const manualDeviceId = ref("");
    const unitSearchText = ref("");
    const currentFacilityId = ref(null);

    const devicesLoading = ref(false);
    const unitsLoading = ref(false);
    const assignmentLoading = ref(false);
    const sendingNotification = ref(false);

    const devices = ref([]);
    const units = ref([]);

    const showMessage = ref(false);
    const message = ref("");
    const messageType = ref("success");

    // Email notification state
    const emailInput = ref("");
    const emailList = ref([]);
    const doneByInput = ref("");

    const filteredUnits = computed(() => {
      if (!unitSearchText.value) return units.value.slice(0, 10);
      const searchLower = unitSearchText.value.toLowerCase();
      return units.value
        .filter((unit) => unit.name?.toLowerCase().includes(searchLower))
        .slice(0, 10);
    });

    const hasAssignments = computed(() => {
      return assignmentSummary.value.totalAssignments > 0;
    });

    const canSendNotification = computed(() => {
      return emailList.value.length > 0 && doneByInput.value.trim().length > 0;
    });

    return {
      currentScreen,
      selectedDevice,
      selectedUnitId,
      assignedDevice,
      assignedUnit,
      manualDeviceId,
      unitSearchText,
      currentFacilityId,
      devicesLoading,
      unitsLoading,
      assignmentLoading,
      sendingNotification,
      devices,
      units,
      filteredUnits,
      assignmentHistory,
      assignmentSummary,
      hasAssignments,
      showMessage,
      message,
      messageType,
      emailInput,
      emailList,
      doneByInput,
      canSendNotification,
      formatDeviceName,
      addAssignment,
      clearAssignments,
      generateAssignmentReport,
    };
  },

  methods: {
    updateFacilityId(facilityId) {
      if (facilityId && facilityId !== this.currentFacilityId) {
        this.currentFacilityId = facilityId;
        this.loadData(facilityId);
      }
    },

    async loadData(facilityId) {
      await Promise.all([
        this.fetchDevices(facilityId),
        this.fetchUnits(facilityId),
      ]);
    },

    goToSummary() {
      this.currentScreen = "summary";
    },

    async fetchDevices(facilityId) {
      this.devicesLoading = true;
      try {
        const result = await this.$apollo.query({
          query: ORGANIZATION_DEVICES,
          variables: {
            id: facilityId,
          },
          fetchPolicy: "network-only",
        });

        this.devices = result.data?.organizationDevices || [];
      } catch (error) {
        console.error("Error fetching devices:", error);
        this.showError("Failed to load devices");
      } finally {
        this.devicesLoading = false;
      }
    },

    async fetchUnits(facilityId) {
      this.unitsLoading = true;
      try {
        const result = await this.$apollo.query({
          query: INVENTORY_SMART_UNITS,
          variables: {
            id: facilityId,
          },
          fetchPolicy: "network-only",
        });

        this.units = result.data?.inventorySmartUnits || [];
      } catch (error) {
        console.error("Error fetching units:", error);
        this.showError("Failed to load units");
      } finally {
        this.unitsLoading = false;
      }
    },

    startDeviceScanning() {
      this.currentScreen = "scanner";
    },

    onDeviceScanned(deviceData) {
      const device = this.findDeviceByData(deviceData);
      if (device) {
        this.onDeviceFound(device);
      } else {
        const assignedUnit = this.units.find((unit) =>
          (unit.devices || []).some(
            (d) =>
              d.externalId === deviceData.externalId || d.id === deviceData.id
          )
        );
        this.showError(
          assignedUnit
            ? `Device already assigned to Unit ${assignedUnit.name}`
            : "Device not found in office inventory"
        );
      }
    },

    onQRDetected(qrData) {
      const deviceId = this.extractDeviceId(qrData.text);
      if (deviceId) {
        this.searchDeviceByShortId(deviceId);
      }
    },

    extractDeviceId(qrText) {
      const match = qrText.match(/([A-Z0-9-]+)/);
      return match ? match[1] : null;
    },

    findDeviceByData(deviceData) {
      return this.devices.find(
        (device) =>
          device.externalId === deviceData.externalId ||
          device.id === deviceData.id
      );
    },

    onDeviceFieldKeydown(event) {
      if (this._barcodeScanCapturing && event.key !== "Enter") {
        event.preventDefault();
        return;
      }
      if (event.key === " " && this.manualDeviceId.length > 0) {
        this._barcodeScanShortId = this.manualDeviceId;
        this._barcodeScanCapturing = true;
        event.preventDefault();
      }
    },

    onDeviceFieldEnter() {
      if (this._barcodeScanCapturing) {
        this.manualDeviceId = this._barcodeScanShortId;
        this._barcodeScanCapturing = false;
        this._barcodeScanShortId = null;
      }
    },

    async searchDeviceById() {
      if (!this.manualDeviceId.trim()) {
        this.showError("Please enter a device ID");
        return;
      }

      await this.searchDeviceByShortId(this.manualDeviceId.trim());
    },

    async searchDeviceByShortId(deviceId) {
      await this.loadData(this.currentFacilityId);
      const lower = deviceId.toLowerCase();
      const device = this.devices.find(
        (d) =>
          d.externalId?.toLowerCase().includes(lower) ||
          d.shortId?.toLowerCase() === lower ||
          d.id === deviceId
      );

      if (device) {
        this.onDeviceFound(device);
        return;
      }

      const assignedUnit = this.units.find((unit) =>
        (unit.devices || []).some(
          (d) =>
            d.externalId?.toLowerCase().includes(lower) ||
            d.shortId?.toLowerCase() === lower ||
            d.id === deviceId
        )
      );
      this.showError(
        assignedUnit
          ? `Device already assigned to Unit ${assignedUnit.name}`
          : "No devices found in office inventory with that ID"
      );
    },

    onDeviceFound(device) {
      this.selectedDevice = device;
      this.currentScreen = "assign";
      this.selectedUnitId = null;
      this.unitSearchText = "";
    },

    async assignDeviceToUnit() {
      if (!this.selectedDevice || !this.selectedUnitId) {
        this.showError("Please select both a device and a unit");
        return;
      }

      const unit = this.units.find((u) => u.id === this.selectedUnitId);
      if (!unit) {
        this.showError("Selected unit not found");
        return;
      }

      this.assignmentLoading = true;
      try {
        const [freshDeviceResult, freshUnitsResult] = await Promise.all([
          this.$apollo.query({
            query: ORGANIZATION_DEVICES,
            variables: { id: this.currentFacilityId },
            fetchPolicy: "network-only",
          }),
          this.$apollo.query({
            query: INVENTORY_SMART_UNITS,
            variables: { id: this.currentFacilityId },
            fetchPolicy: "network-only",
          }),
        ]);

        const freshDevices = freshDeviceResult.data?.organizationDevices || [];
        const freshUnits = freshUnitsResult.data?.inventorySmartUnits || [];
        this.devices = freshDevices;
        this.units = freshUnits;

        const deviceInFreshInventory = freshDevices.some(
          (d) => d.id === this.selectedDevice.id
        );
        const assignedUnit = !deviceInFreshInventory
          ? freshUnits.find((u) =>
              (u.devices || []).some((d) => d.id === this.selectedDevice.id)
            )
          : null;

        if (
          !deviceInFreshInventory &&
          (freshDevices.length > 0 || assignedUnit)
        ) {
          this.showError(
            assignedUnit
              ? `Device already assigned to Unit ${assignedUnit.name}`
              : "Device already assigned to another unit"
          );
          return;
        }

        await this.$apollo.mutate({
          mutation: UPDATE_DEVICE,
          variables: {
            id: this.selectedDevice.id,
            externalId: this.selectedDevice.externalId,
            organizationId: this.selectedUnitId,
            type: this.selectedDevice.type,
            disposition: this.selectedDevice.disposition,
          },
        });

        this.addAssignment(
          {
            id: this.selectedDevice.id,
            name: this.formatDeviceName(this.selectedDevice),
            externalId: this.selectedDevice.externalId,
          },
          {
            id: unit.id,
            name: unit.name,
          }
        );

        this.assignedDevice = this.selectedDevice;
        this.assignedUnit = unit;

        this.currentScreen = "assigned";
        this.showSuccess(
          `Successfully assigned ${this.formatDeviceName(
            this.selectedDevice
          )} to ${unit.name}`
        );

        await this.fetchDevices(this.currentFacilityId);
      } catch (error) {
        this.showError("Failed to assign device to unit");
      } finally {
        this.assignmentLoading = false;
      }
    },

    scanNextDevice() {
      this.selectedDevice = null;
      this.selectedUnitId = null;
      this.assignedDevice = null;
      this.assignedUnit = null;
      this.manualDeviceId = "";
      this.unitSearchText = "";
      this.currentScreen = "scan";
    },

    completeAssignment() {
      if (this.assignmentSummary.totalAssignments > 0) {
        this.currentScreen = "summary";
      } else {
        this.finishAndClear();
      }
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email.trim());
    },

    addEmail() {
      const email = this.emailInput.trim();
      if (!email) return;

      if (!this.isValidEmail(email)) {
        this.showError("Please enter a valid email address");
        return;
      }

      if (this.emailList.includes(email)) {
        this.showError("Email already added");
        return;
      }

      this.emailList.push(email);
      this.emailInput = "";
      this.showSuccess("Email added successfully");
    },

    removeEmail(index) {
      this.emailList.splice(index, 1);
    },

    async sendNotification() {
      if (!this.canSendNotification) {
        this.showError("Please add at least one email and enter your name");
        return;
      }

      this.sendingNotification = true;
      try {
        const devicesDetails = this.assignmentHistory.map((assignment) => ({
          unitName: assignment.unitName,
          deviceName: assignment.deviceName,
        }));

        const variables = {
          doneBy: this.doneByInput.trim(),
          facilityId: this.currentFacilityId,
          emails: [...this.emailList],
          devicesDetails,
        };

        await this.$apollo.mutate({
          mutation: SEND_PREINSTALLED_DEVICE_MOBILE_SUMMARY,
          variables,
        });

        this.showSuccess("Notification sent successfully!");

        this.emailList = [];
        this.emailInput = "";
        this.doneByInput = "";

        setTimeout(() => {
          this.finishAndClear();
        }, 2000);
      } catch (error) {
        console.error("Error sending notification:", error);
        this.showError("Failed to send notification. Please try again.");
      } finally {
        this.sendingNotification = false;
      }
    },

    finishAndClear() {
      this.clearAssignments();
      this.selectedDevice = null;
      this.selectedUnitId = null;
      this.assignedDevice = null;
      this.assignedUnit = null;
      this.manualDeviceId = "";
      this.unitSearchText = "";
      this.emailList = [];
      this.emailInput = "";
      this.doneByInput = "";
      this.currentScreen = "scan";
      this.showSuccess("Session completed successfully!");
    },

    getStatusColor(status) {
      switch (status?.toLowerCase()) {
        case "ok":
          return "success";
        case "offline":
          return "error";
        case "warning":
          return "warning";
        default:
          return "grey";
      }
    },

    showSuccess(msg) {
      this.message = msg;
      this.messageType = "success";
      this.showMessage = true;
    },

    showError(msg) {
      this.message = msg;
      this.messageType = "error";
      this.showMessage = true;
    },
  },
};
</script>

<style scoped>
.mobile-inventory-content {
  min-height: 100vh;
  background-color: #f5f5f5;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  padding: 0;
  margin: 0;
}

.screen-container {
  padding: 24px 16px;
  margin: 0 auto;
  width: 95vw;
}

.scanner-screen {
  position: relative;
  width: 95vw;
  height: 100vh;
  background: #000;
}

.screen-header {
  text-align: center;
  margin-bottom: 32px;
}

.screen-header h2 {
  color: #1976d2;
  margin: 0 0 8px 0;
  font-size: 1.5rem;
}

.screen-header p {
  color: #666;
  margin: 0;
  line-height: 1.5;
}

.barcode-icon {
  text-align: center;
  margin: 32px 0;
}

.scan-actions {
  margin-top: 32px;
}

.selected-device-info {
  margin-bottom: 24px;
}

.device-card,
.unit-card,
.assignment-summary,
.assignment-list,
.notification-section {
  color: #000000;
}

.device-card,
.unit-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  margin: 8px 0;
}

.device-card.selected,
.unit-card.selected {
  border-color: #1976d2;
  background-color: #f3f9ff;
}

.device-info {
  flex: 1;
}

.unit-search-section {
  margin-bottom: 24px;
}

.unit-search {
  background: white;
}

.assign-actions {
  margin-top: 32px;
}

.success-header {
  text-align: center;
  margin-bottom: 32px;
}

.success-header h2 {
  color: #4caf50;
  margin: 16px 0 0 0;
  font-size: 1.5rem;
}

.assignment-summary {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.summary-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.summary-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.continue-message {
  text-align: center;
  margin-bottom: 24px;
  color: #666;
}

.next-actions {
  margin-top: 32px;
}

.summary-header {
  text-align: center;
  margin-bottom: 24px;
}

.summary-header h2 {
  color: #1976d2;
  margin: 0 0 8px 0;
  font-size: 1.5rem;
}

.assignment-list {
  background: white;
  border-radius: 8px;
  margin-bottom: 24px;
  overflow: hidden;
}

.assignment-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.assignment-item:last-child {
  border-bottom: none;
}

.assignment-details {
  flex: 1;
}

.unit-name {
  font-weight: 600;
  color: #1976d2;
  margin-bottom: 4px;
}

.summary-actions {
  margin-top: 32px;
}

.notification-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.notification-title {
  color: #1976d2;
  margin: 0 0 8px 0;
  font-size: 1.2rem;
  font-weight: 600;
}

.notification-description {
  color: #666;
  margin: 0 0 16px 0;
  font-size: 0.9rem;
  line-height: 1.4;
}

.email-input-section {
  margin-bottom: 16px;
}

.email-list {
  min-height: 32px;
  padding: 8px 0;
}

@media (max-width: 768px) {
  .screen-container {
    padding: 16px 12px;
  }

  .device-card {
    padding: 12px;
  }

  .notification-section {
    padding: 16px;
  }

  .notification-title {
    font-size: 1.1rem;
  }
}

.loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-content {
  text-align: center;
}
</style>
